import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDanceStore } from '../store/useDanceStore';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { DanceState } from '../types';

export const ConfirmDialog: FC = () => {
  const { t } = useTranslation();
  const confirmDialog = useDanceStore((state: DanceState) => state.confirmDialog);
  const closeConfirm = useDanceStore((state: DanceState) => state.closeConfirm);

  const [selectedKey, setSelectedKey] = useState<string | undefined>(confirmDialog?.defaultOptionKey);

  React.useEffect(() => {
    if (confirmDialog?.defaultOptionKey) {
      setSelectedKey(confirmDialog.defaultOptionKey);
    }
  }, [confirmDialog?.defaultOptionKey]);

  const handleConfirm = useCallback(() => {
    if (confirmDialog?.onConfirm) confirmDialog.onConfirm(selectedKey);
    closeConfirm();
  }, [confirmDialog, closeConfirm, selectedKey]);

  const handleCancel = useCallback(() => {
    if (confirmDialog?.isNonDismissible) return; // Prevent closing if non-dismissible
    if (confirmDialog?.onCancel) confirmDialog.onCancel();
    closeConfirm();
  }, [confirmDialog, closeConfirm]);

  const handleNeutral = useCallback(() => {
    if (confirmDialog?.onNeutral) confirmDialog.onNeutral();
    closeConfirm();
  }, [confirmDialog, closeConfirm]);

  const handleExtra = useCallback(() => {
    if (confirmDialog?.onExtra) confirmDialog.onExtra();
    closeConfirm();
  }, [confirmDialog, closeConfirm]);

  React.useEffect(() => {
    if (!confirmDialog?.isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      if (e.key === 'Escape') {
        handleCancel();
      } else if (e.key === 'Enter') {
        if (confirmDialog.isLoading) return; // Prevent confirming via Enter during loading
        handleConfirm();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmDialog?.isOpen, confirmDialog?.isLoading, handleCancel, handleConfirm]);

  if (!confirmDialog || !confirmDialog.isOpen) return null;

  const { title, message, isAlert, confirmLabel, cancelLabel, neutralLabel, extraLabel, showCheckbox, checkboxLabel, checkboxValue, onCheckboxToggle, showOptions, options, isNonDismissible, isLoading } = confirmDialog;

  return (
    <div className="confirm-dialog-overlay" onClick={handleCancel}>
      <div className="glass-panel confirm-dialog-content" onClick={e => e.stopPropagation()}>
        <div className="flex-between confirm-dialog-header">
          <div className="confirm-dialog-header-left">
            {isLoading ? (
              <Loader2 size={24} className="confirm-dialog-spinner" />
            ) : (
              <AlertTriangle size={24} color={isAlert ? "#38bdf8" : "#fbbf24"} />
            )}
            <h2 className="confirm-dialog-title">{title}</h2>
          </div>
          {!isNonDismissible && (
            <button onClick={handleCancel} className="confirm-dialog-close-btn">
              <X size={20} />
            </button>
          )}
        </div>
        
        <div className="confirm-dialog-body">
          {message}
        </div>

        {!isLoading && showOptions && options && (
          <div className="confirm-dialog-options-list">
            {options.map(opt => (
              <div 
                key={opt.key}
                onClick={() => setSelectedKey(opt.key)}
                className={`confirm-dialog-option ${selectedKey === opt.key ? 'selected' : ''}`}
              >
                <div className={`confirm-dialog-radio-outer ${selectedKey === opt.key ? 'selected' : ''}`}>
                  {selectedKey === opt.key && (
                    <div className="confirm-dialog-radio-inner" />
                  )}
                </div>
                <div className="confirm-dialog-option-text">
                  <span className={`confirm-dialog-option-label ${selectedKey === opt.key ? 'selected' : ''}`}>
                    {opt.label}
                  </span>
                  <span className="confirm-dialog-option-desc">
                    {opt.desc}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!isLoading && (
          <div className="confirm-dialog-footer">
            {neutralLabel && (
              <button 
                onClick={handleNeutral}
                className="confirm-dialog-btn-neutral"
              >
                {neutralLabel}
              </button>
            )}

            {extraLabel && (
              <button 
                onClick={handleExtra}
                className="confirm-dialog-btn-extra"
              >
                {extraLabel}
              </button>
            )}

            <button 
              className={`primary confirm-dialog-btn-primary ${isAlert ? 'alert' : ''}`}
              onClick={handleConfirm}
            >
              {isAlert ? (confirmLabel || t('common.ok')) : (confirmLabel || t('common.confirm'))}
            </button>

            {!isAlert && (
              <button 
                className="dialog-cancel-btn confirm-dialog-btn-cancel"
                onClick={handleCancel}
              >
                {cancelLabel || t('common.cancel')}
              </button>
            )}
          </div>
        )}

        {!isLoading && showCheckbox && (
          <label className="confirm-dialog-checkbox-label">
            <span className="confirm-dialog-checkbox-text">{checkboxLabel}</span>
            <input 
              type="checkbox"
              checked={!!checkboxValue}
              onChange={(e) => onCheckboxToggle?.(e.target.checked)}
              style={{ display: 'none' }}
            />
            <div className={`confirm-dialog-checkbox-box ${checkboxValue ? 'checked' : ''}`}>
              {checkboxValue && <div className="confirm-dialog-checkbox-dot" />}
            </div>
          </label>
        )}
      </div>
    </div>
  );
};
